import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { AuthPage } from "@/client/components/auth/auth-page";
import { SignInForm } from "@/client/components/auth/sign-in-form";
import { Button } from "@/client/components/ui/button";
import { useSession } from "@/client/hooks/use-session";
import { apiJson } from "@/client/lib/api";

export const Route = createFileRoute("/agents/authorize")({
  component: AuthorizeAgent,
});
interface Details {
  clientId: string;
  clientName: string;
  clientUri: string | null;
  canRequestProposals: boolean;
  stories: {
    id: string;
    name: string;
    automaticAiChecks: boolean;
    ownerUserId: string;
  }[];
}
function AuthorizeAgent() {
  const { status, user } = useSession();
  const [oauthQuery] = useState(() => window.location.search.slice(1));
  const [details, setDetails] = useState<Details | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [canPropose, setCanPropose] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (status !== "signed-in") return;
    const controller = new AbortController();
    apiJson<Details>("/api/agents/consent-details", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ oauthQuery }),
      signal: controller.signal,
    })
      .then(setDetails)
      .catch((e) => {
        if (!controller.signal.aborted) setError(e.message);
      });
    return () => controller.abort();
  }, [status, oauthQuery]);
  async function decide(accept: boolean) {
    setBusy(true);
    setError("");
    try {
      const result = await apiJson<{ url: string }>("/api/agents/consent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          oauthQuery,
          accept,
          storyIds: selected,
          canPropose,
        }),
      });
      // The provider validates the client's registered redirect URI, including
      // native callback schemes. Never redirect using query parameters directly.
      window.location.assign(result.url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Connection failed");
      setBusy(false);
    }
  }
  return (
    <div className="h-full overflow-y-auto">
      <AuthPage
        title={
          status === "signed-in" ? "Connect an agent" : "Sign in to connect"
        }
        description={
          status === "signed-in"
            ? `Signed in as ${user?.email}`
            : "Sign in to Renteller, then choose which stories this agent can read."
        }
      >
        {status === "loading" ? (
          <p role="status">Loading…</p>
        ) : status !== "signed-in" ? (
          <SignInForm />
        ) : (
          <div className="space-y-5 pb-8">
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
            {!details && !error && (
              <p role="status" className="text-sm text-muted-foreground">
                Checking the connection request…
              </p>
            )}
            {details && (
              <>
                <div className="space-y-1">
                  <p className="font-medium">{details.clientName}</p>
                  <p className="break-all text-xs text-muted-foreground">
                    Client ID: {details.clientId}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    This agent will receive the writing and context of the
                    stories you select. Its provider handles model access and
                    billing.
                  </p>
                </div>
                <fieldset className="space-y-2">
                  <legend className="mb-2 text-sm font-medium">
                    Stories to share
                  </legend>
                  {details.stories.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      Create or join a story in Renteller, then start this
                      connection again.
                    </p>
                  )}
                  <div className="max-h-52 space-y-2 overflow-y-auto">
                    {details.stories.map((s) => (
                      <label
                        key={s.id}
                        className="flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 accent-primary"
                          checked={selected.includes(s.id)}
                          disabled={
                            busy ||
                            (!selected.includes(s.id) && selected.length >= 50)
                          }
                          onChange={(e) =>
                            setSelected((prev) =>
                              e.target.checked
                                ? [...prev, s.id]
                                : prev.filter((id) => id !== s.id),
                            )
                          }
                        />
                        <span className="min-w-0 break-words">
                          {s.name}
                          <span className="mt-1 block text-xs text-muted-foreground">
                            Automatic Renteller checks{" "}
                            {s.automaticAiChecks
                              ? "on · use Renteller model credits"
                              : "off"}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                {details.canRequestProposals && (
                  <label className="flex cursor-pointer items-start gap-3 text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-primary"
                      checked={canPropose}
                      disabled={busy}
                      onChange={(e) => setCanPropose(e.target.checked)}
                    />
                    <span>
                      Allow scene suggestions
                      <span className="mt-1 block text-xs text-muted-foreground">
                        You review and apply every edit in Renteller. The agent
                        cannot edit directly.
                      </span>
                    </span>
                  </label>
                )}
                <p className="text-xs text-muted-foreground">
                  New stories are never shared automatically. Disconnect at any
                  time from Account → Connected agents. Reading and submitting
                  suggestions use no Renteller model credits.
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    disabled={busy}
                    onClick={() => void decide(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1"
                    disabled={busy || !selected.length}
                    onClick={() => void decide(true)}
                  >
                    {busy ? "Connecting…" : "Connect"}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </AuthPage>
    </div>
  );
}
